import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Fade,
  Autocomplete
} from '@mui/material';
import type { Book, BookFormData, Category } from '../types';
import { bookService, categoryService } from '../services/bookService';
import { logger } from '../utils/logger';


interface BookFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (book: Book) => void;
  onError?: (error: string) => void;
  book?: Book | null;
  title: string;
}

export const BookForm: React.FC<BookFormProps> = ({ open, onClose, onSave, onError, book, title }) => {
  const [formData, setFormData] = useState<BookFormData>({
    title: '',
    author: '',
    isbn: '',
    publisher: '',
    publishYear: undefined,
    subjects: [],
    categoryId: '', // Backward compatibility
    categoryIds: [], // New: array of category IDs
    summary: '',
    notes: '',
    totalCopies: 1,
    availableCopies: 1
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subjectInput, setSubjectInput] = useState('');
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadCategories();
      if (book) {
        // Support both categoryIds (new) and categoryId (backward compatibility)
        const categoryIds = book.categoryIds || (book.categoryId ? [book.categoryId] : []);
        setFormData({
          title: book.title,
          author: book.author,
          isbn: book.isbn || '',
          publisher: book.publisher || '',
          publishYear: book.publishYear,
          subjects: book.subjects,
          categoryId: categoryIds.length > 0 ? categoryIds[0] : '', // Backward compatibility
          categoryIds: categoryIds, // New: array of category IDs
          summary: book.summary || '',
          notes: book.notes || '',
          totalCopies: book.totalCopies,
          availableCopies: book.availableCopies
        });
      } else {
        setFormData({
          title: '',
          author: '',
          isbn: '',
          publisher: '',
          publishYear: undefined,
          subjects: [],
          categoryId: '',
          categoryIds: [],
          summary: '',
          notes: '',
          totalCopies: 1,
          availableCopies: 1
        });
      }
      setError(null);
    }
  }, [open, book]);

  const loadCategories = async () => {
    try {
      // Get all categories (no pagination limit for dropdown/autocomplete)
      const response = await categoryService.getCategories({ page: 1, pageSize: 1000 });
      // Backend returns object with categories array
      const categoriesList = response.categories || [];
      logger.log('📚 Categories loaded in BookForm:', categoriesList.length, categoriesList);
      setCategories(categoriesList);
      
      // Set categoryIds after categories are loaded
      if (book) {
        const categoryIds = book.categoryIds || (book.categoryId ? [book.categoryId] : []);
        setFormData(prev => ({
          ...prev,
          categoryId: categoryIds.length > 0 ? categoryIds[0] : '', // Backward compatibility
          categoryIds: categoryIds
        }));
      }
    } catch (err) {
      logger.error('Failed to load categories:', err);
      setCategories([]); // Set empty array as fallback
    }
  };

  const handleInputChange = (field: keyof BookFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'publishYear' ? (value ? parseInt(value) : undefined) :
               field === 'totalCopies' ? parseInt(value) || 0 :
               field === 'availableCopies' ? parseInt(value) || 0 :
               value
    }));
  };

  const handleSelectChange = (field: keyof BookFormData) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubjectAdd = () => {
    if (subjectInput.trim() && !formData.subjects.includes(subjectInput.trim())) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subjectInput.trim()]
      }));
      setSubjectInput('');
    }
  };

  const handleSubjectRemove = (subjectToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(subject => subject !== subjectToRemove)
    }));
  };

  const handleSubjectKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubjectAdd();
    }
  };

  const validateForm = (): string | null => {
    logger.log('Form data before validation:', {
      title: formData.title,
      author: formData.author,
      totalCopies: formData.totalCopies,
      availableCopies: formData.availableCopies,
      subjects: formData.subjects,
      subjectsLength: formData.subjects?.length || 0,
      totalCopiesType: typeof formData.totalCopies,
      availableCopiesType: typeof formData.availableCopies
    });
    
    if (!formData.title.trim()) return 'Tên sách là bắt buộc';
    if (!formData.author.trim()) return 'Tác giả là bắt buộc';
    if (formData.totalCopies < 0) return 'Tổng số bản sao không được âm';
    if (formData.availableCopies < 0) return 'Số bản sao có sẵn không được âm';
    if (formData.availableCopies > formData.totalCopies) {
      logger.log('Validation failed:', {
        availableCopies: formData.availableCopies,
        totalCopies: formData.totalCopies,
        availableCopiesType: typeof formData.availableCopies,
        totalCopiesType: typeof formData.totalCopies,
        comparison: `${formData.availableCopies} > ${formData.totalCopies} = ${formData.availableCopies > formData.totalCopies}`
      });
      return 'Số bản sao có sẵn không được vượt quá tổng số bản sao';
    }
    if (formData.publishYear && (formData.publishYear < 1000 || formData.publishYear > new Date().getFullYear() + 1)) {
      return 'Năm xuất bản không hợp lệ';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let savedBook: Book;
      if (book) {
        savedBook = await bookService.updateBook(book.id, formData);
      } else {
        savedBook = await bookService.createBook(formData);
      }

      onSave(savedBook);
      
      // Add small delay to ensure proper focus management
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : (err?.error?.message || err?.error || 'Có lỗi xảy ra khi lưu sách');
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (event: any, reason?: string) => {
    // Chỉ đóng Dialog nếu không phải backdrop click
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      TransitionComponent={Fade}
      TransitionProps={{ 
        timeout: 300,
        onEntering: () => {
          // Blur any focused element early to prevent aria-hidden issues
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        },
        onEntered: () => {
          // Focus vào input đầu tiên khi Dialog mở
          if (firstInputRef.current) {
            setTimeout(() => firstInputRef.current?.focus(), 100);
          }
        },
        onExiting: () => {
          // Blur focus when Dialog is closing to prevent aria-hidden issues
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }
      }}
      BackdropProps={{
        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField
            ref={firstInputRef}
            fullWidth
            label="Tên sách *"
            value={formData.title}
            onChange={handleInputChange('title')}
            required
          />
          <TextField
            fullWidth
            label="Tác giả *"
            value={formData.author}
            onChange={handleInputChange('author')}
            required
          />
          <TextField
            fullWidth
            label="ISBN"
            value={formData.isbn}
            onChange={handleInputChange('isbn')}
          />
          <TextField
            fullWidth
            label="Nhà xuất bản"
            value={formData.publisher}
            onChange={handleInputChange('publisher')}
          />
          <TextField
            fullWidth
            label="Năm xuất bản"
            type="number"
            value={formData.publishYear || ''}
            onChange={handleInputChange('publishYear')}
          />
          <Autocomplete
            multiple
            options={categories || []}
            getOptionLabel={(option) => option.name}
            value={categories.filter(cat => formData.categoryIds?.includes(cat.id)) || []}
            onChange={(event, newValue) => {
              const categoryIds = newValue.map(cat => cat.id);
              setFormData(prev => ({
                ...prev,
                categoryIds: categoryIds,
                categoryId: categoryIds.length > 0 ? categoryIds[0] : '' // Backward compatibility
              }));
            }}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                label="Danh mục"
                placeholder="Chọn danh mục (có thể chọn nhiều)"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
          <TextField
            fullWidth
            label="Tổng số bản sao *"
            type="number"
            value={formData.totalCopies}
            onChange={handleInputChange('totalCopies')}
            required
            inputProps={{ min: 0 }}
          />
          <TextField
            fullWidth
            label="Số bản sao có sẵn *"
            type="number"
            value={formData.availableCopies}
            onChange={handleInputChange('availableCopies')}
            required
            inputProps={{ min: 0, max: formData.totalCopies }}
          />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="subtitle2" gutterBottom>
              Chủ đề
            </Typography>
            <Box display="flex" gap={1} mb={1}>
              <TextField
                placeholder="Nhập chủ đề và nhấn Enter"
                value={subjectInput}
                onChange={(e) => setSubjectInput(e.target.value)}
                onKeyPress={handleSubjectKeyPress}
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button onClick={handleSubjectAdd} variant="outlined" size="small">
                Thêm
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.subjects.map((subject, index) => (
                <Chip
                  key={index}
                  label={subject}
                  onDelete={() => handleSubjectRemove(subject)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              fullWidth
              label="Tóm tắt"
              multiline
              rows={3}
              value={formData.summary}
              onChange={handleInputChange('summary')}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              fullWidth
              label="Ghi chú"
              multiline
              rows={2}
              value={formData.notes}
              onChange={handleInputChange('notes')}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Hủy
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
